import { AlertTriangle, Info, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * What the server says about the paper sitting on this job (server:
 * services/formEngine/jurisdictionFormMismatch.ts).
 *
 *   wrong_form   — #260. Paper jurisdiction, wrong paper.
 *   portal_paper — #1537. The site files through a portal, so this paper files
 *                  nowhere.
 */
export type FormVerdict =
    | {
          kind: "wrong_form";
          currentFormLabel: string;
          correctFormLabel: string;
          correctTemplateId: string;
      }
    | {
          kind: "portal_paper";
          currentFormLabel: string;
          platformLabel: string;
          hasCheatSheet: boolean;
      };

/**
 * The Station 1 banner. Extracted from the Documents station (#1537) so the two
 * variants' copy is testable on its own — the wording is the contract with the
 * approved mockup, and it lived 5,000 lines inside a page component.
 *
 * Both variants call the SAME fix: POST /backflow/job/:id/regenerate-report,
 * which archives the superseded paperwork and re-renders against the site's
 * current jurisdiction. On a paper site that means the right form; on a portal
 * site it means no form and a cheat sheet.
 */
export function FormVerdictBanner({
    verdict,
    cityName,
    pending,
    onFix,
}: {
    verdict: FormVerdict | null;
    cityName: string | null;
    pending: boolean;
    onFix: () => void;
}) {
    if (!verdict) return null;

    const spinner = pending ? (
        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
    ) : (
        <RefreshCw className="mr-1 h-3.5 w-3.5" />
    );

    if (verdict.kind === "wrong_form") {
        return (
            <div className="flex flex-col gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-amber-900 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-200 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                    <p className="text-[12.5px] leading-snug">
                        <b>Wrong form.</b> This report is on the <b>{verdict.currentFormLabel}</b> form, but this site
                        files to <b>{cityName ?? "this jurisdiction"}</b> — it should be on the{" "}
                        <b>{verdict.correctFormLabel}</b>.
                    </p>
                </div>
                <Button
                    size="sm"
                    className="w-full flex-none bg-amber-700 text-white hover:bg-amber-800 sm:w-auto"
                    disabled={pending}
                    onClick={onFix}
                >
                    {spinner}
                    Reformat to the {verdict.correctFormLabel}
                </Button>
            </div>
        );
    }

    // Teal, not amber: this is not the same problem as the wrong paper above,
    // and the fix removes paperwork rather than replacing it.
    return (
        <div className="flex flex-col gap-2 rounded-xl border border-teal-300 bg-teal-50 px-3.5 py-2.5 text-teal-900 dark:border-teal-800/70 dark:bg-teal-950/40 dark:text-teal-200 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2">
                <Info className="mt-0.5 h-4 w-4 flex-none" />
                <p className="text-[12.5px] leading-snug">
                    <b>This site files through {verdict.platformLabel}.</b> <b>{verdict.currentFormLabel}</b> is a paper
                    form — nothing files it anywhere.
                    {verdict.hasCheatSheet
                        ? " The cheat sheet is already here; re-rendering clears the leftover paper and rebuilds it from the same readings."
                        : " The readings still have to be entered in the portal. Re-rendering removes the paper copies and builds the Online Reporting Cheat Sheet the tester enters from."}
                </p>
            </div>
            <Button
                size="sm"
                className="w-full flex-none bg-teal-700 text-white hover:bg-teal-800 sm:w-auto"
                disabled={pending}
                onClick={onFix}
            >
                {spinner}
                Re-render for {verdict.platformLabel}
            </Button>
        </div>
    );
}
